import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import "./SaveSheetModal.scss";
import { getAccessToken } from "../../awsAuth";
import saveIcon from "./img/save.png";
import savedIcon from "./img/saved.png";

const API = process.env.REACT_APP_API_BASE || "http://localhost:5000";
const DEFAULT_COLLECTION_ID = "all";

function useBodyScrollLock(lock) {
  useEffect(() => {
    const { body, documentElement: html } = document;

    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      paddingRight: body.style.paddingRight,
    };

    if (lock) {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const scrollbarW = window.innerWidth - html.clientWidth;
      if (scrollbarW > 0) {
        body.style.paddingRight = `${scrollbarW}px`;
      }

      body.classList.add("cv-modal-open");
      html.classList.add("cv-scroll-lock");

      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.width = "100%";
      body.style.overflow = "hidden";

      return () => {
        body.style.overflow = prev.overflow;
        body.style.position = prev.position;
        body.style.top = prev.top;
        body.style.width = prev.width;
        body.style.paddingRight = prev.paddingRight;

        body.classList.remove("cv-modal-open");
        html.classList.remove("cv-scroll-lock");

        const y = Math.abs(parseInt(prev.top || "0")) || scrollY;
        window.scrollTo(0, y);
      };
    }
    return () => {};
  }, [lock]);
}

export default function SaveSheetModal({
  open,
  onClose,
  userId,
  postId,
  type = "listing",
  thumbnail,
  title = "Збереження",
  onChanged,
}) {
  useBodyScrollLock(open);

  // Responsive
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 640px)").matches);
  const [sheetState, setSheetState] = useState("half");
  const sheetRef = useRef(null);
  const headerRef = useRef(null);
  const dragRef = useRef({ startY: 0, startH: 0, dragging: false, lastDelta: 0, startTime: 0, endTime: 0 });

  useEffect(() => {
    const mm = window.matchMedia("(max-width: 640px)");
    const handler = () => setIsMobile(mm.matches);
    if (mm.addEventListener) mm.addEventListener("change", handler); else mm.addListener(handler);
    return () => { if (mm.removeEventListener) mm.removeEventListener("change", handler); else mm.removeListener(handler); };
  }, []);

  const [isClosing, setIsClosing] = useState(false);
  const requestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, 200);
  }, [isClosing, onClose]);

  // Sheet sizing
  useEffect(() => {
    if (!isMobile || !open) return;
    const sheet = sheetRef.current;
    if (sheet) sheet.style.setProperty("--sv-sheet-height", sheetState === "full" ? "100vh" : "62vh");
  }, [isMobile, open, sheetState]);

  // Data
  const [collections, setCollections] = useState([]);
  const [currentCol, setCurrentCol] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [currentLoaded, setCurrentLoaded] = useState(false);

  // Creator
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const loadCollections = useCallback(async () => {
    if (!open || !userId) return;
    setLoading(true); setErr("");
    try {
      const token = await getAccessToken().catch(() => null);
      const r = await axios.get(`/api/saved/collections/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let cols = Array.isArray(r.data) ? r.data : [];
      cols = cols.filter(c => c.collectionId !== DEFAULT_COLLECTION_ID);
      cols.unshift({ collectionId: DEFAULT_COLLECTION_ID, name: "Усі" });
      setCollections(cols);
    } catch {
      setCollections([{ collectionId: DEFAULT_COLLECTION_ID, name: "Усі" }]);
      setErr("Не вдалося завантажити колекції");
    } finally {
      setLoading(false);
    }
  }, [open, userId]);

  const locateCurrent = useCallback(async () => {
    if (!open || !userId || !postId) return;
    try {
      const token = await getAccessToken().catch(() => null);
      const r = await axios.get(`/api/saved/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = Array.isArray(r.data) ? r.data : [];
      const found = items.find(it => String(it.postId) === String(postId));
      const col = found?.collectionId || (found ? DEFAULT_COLLECTION_ID : null);
      setCurrentCol(col ?? null);
    } catch {
      setCurrentCol(null);
    } finally {
      setCurrentLoaded(true);
    }
  }, [open, userId, postId]);

  useEffect(() => {
    if (open) {
      setSheetState("half");
      setCreatorOpen(false);
      setNewName("");
      setCurrentLoaded(false);
      loadCollections();
      locateCurrent();
    }
  }, [open, loadCollections, locateCurrent]);

  // Actions
  const doSaveTo = async (collectionId) => {
    if (!userId || !postId) return;

    const token = await getAccessToken().catch(() => null);
    const headers = { Authorization: `Bearer ${token}` };

    if (currentCol && currentCol === collectionId) {
      try {
        await axios.delete(`/api/saved`, { data: { userId, postId, type }, headers });
        setCurrentCol(null);
        onChanged?.({ saved: false });
        requestClose();
      } catch {
        setErr("Не вдалося прибрати із збережених");
      }
      return;
    }

    try {
      if (!currentCol) {
        await axios.post(`/api/saved`, { userId, postId, type, collectionId }, { headers });
      } else {
        await axios.put(`/api/saved/move`, { userId, postId, collectionId }, { headers });
      }

      setCurrentCol(collectionId);
      onChanged?.({ saved: true, collectionId });
      requestClose();
    } catch {
      setErr("Операція не вдалася");
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || !userId) return;
    try {
      setCreating(true);
      const token = await getAccessToken().catch(() => null);
      const r = await axios.post(`/api/saved/collections`, { userId, name }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const created = r.data;
      await loadCollections();
      setCreatorOpen(false);
      setNewName("");
      await doSaveTo(created.collectionId);
    } catch (e) {
      setErr(e?.response?.data?.error || "Не вдалося створити колекцію");
    } finally {
      setCreating(false);
    }
  };

  const onGrabStart = (clientY) => {
    const sheet = sheetRef.current; if (!sheet) return;
    dragRef.current = {
      startY: clientY,
      startH: sheetState === "full" ? 100 : 62,
      dragging: true,
      lastDelta: 0,
      startTime: performance.now(),
      endTime: 0,
    };
    sheet.classList.add("sv-modal--dragging");
  };
  const onGrabMove = (clientY) => {
    if (!dragRef.current.dragging) return;
    const deltaY = clientY - dragRef.current.startY;
    dragRef.current.lastDelta = deltaY;
    const screenH = Math.max(window.innerHeight, 1);
    const deltaVH = (deltaY / screenH) * 100;
    let nextVH = dragRef.current.startH - deltaVH;
    nextVH = Math.max(46, Math.min(100, nextVH));
    const sheet = sheetRef.current; if (sheet) sheet.style.setProperty("--sv-sheet-height", `${nextVH}vh`);
  };
  const onGrabEnd = () => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    const delta = dragRef.current.lastDelta;
    const sheet = sheetRef.current; if (sheet) sheet.classList.remove("sv-modal--dragging");

    const CLOSE_DRAG_PX = 90;
    const SNAP_DRAG_PX  = 70;

    if (sheetState === "half") {
      if (delta > CLOSE_DRAG_PX) { requestClose(); return; }
      if (-delta > SNAP_DRAG_PX) { setSheetState("full"); requestAnimationFrame(() => sheet?.style.setProperty("--sv-sheet-height", "100vh")); return; }
      requestAnimationFrame(() => sheet?.style.setProperty("--sv-sheet-height", "62vh"));
    } else {
      if (delta > SNAP_DRAG_PX) { setSheetState("half"); requestAnimationFrame(() => sheet?.style.setProperty("--sv-sheet-height", "62vh")); return; }
      requestAnimationFrame(() => sheet?.style.setProperty("--sv-sheet-height", "100vh"));
    }
  };

  useEffect(() => {
    if (!isMobile || !open) return;
    const el = headerRef.current; if (!el) return;

    const onPointerDown = (e) => {
      e.preventDefault();
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      onGrabStart(y);
      window.addEventListener("pointermove", onPointerMove, { passive: false });
      window.addEventListener("pointerup", onPointerUp, { passive: false });
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("touchend", onTouchEnd, { passive: false });
    };
    const onPointerMove = (e) => onGrabMove(e.clientY);
    const onPointerUp = () => {
      onGrabEnd();
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
    const onTouchMove = (e) => onGrabMove(e.touches[0].clientY);
    const onTouchEnd = () => {
      onGrabEnd();
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };

    el.addEventListener("pointerdown", onPointerDown, { passive: false });
    el.addEventListener("touchstart", onPointerDown, { passive: false });

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("touchstart", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [isMobile, open, sheetState]);

  if (!open) return null;

  // backdrop click
  const onBackdropClick = (e) => { if (e.target === e.currentTarget) requestClose(); };
  const stop = (e) => { e.stopPropagation(); };

  return (
    <div
      className={`sv-backdrop ${isMobile ? "is-mobile" : ""} ${isClosing ? "is-closing" : "is-opening"}`}
      onClick={onBackdropClick}
      onMouseDown={stop}
    >
      <div
        ref={sheetRef}
        className={`sv-modal ${isMobile ? `is-${sheetState}` : ""} ${isClosing ? "is-closing" : "is-opening"}`}
        role="dialog"
        aria-modal="true"
        onClick={stop}
        onMouseDown={stop}
      >
        {/* Header */}
        <div ref={headerRef} className="sv-header">
          <div className="sv-grabber" aria-hidden />
          <div className="sv-title">
            <div className="sv-thumb">
              {thumbnail ? <img src={thumbnail} alt="" /> : <div className="sv-thumb__ph" />}
            </div>
            <div className="sv-title-text">
              <div className="sv-title-main">{title}</div>
              <div className="sv-title-sub">Приватні</div>
            </div>
            {!isMobile && <button className="sv-close" onClick={requestClose} aria-label="Закрити">✕</button>}
          </div>
        </div>

        {/* Колекції / хедер секції */}
        <div className="sv-section sv-appear" style={{ "--stagger": "12ms" }} onClick={stop}>
          <div className="sv-section-title">Колекції</div>
          <button
            className="sv-new"
            onClick={(e) => { e.stopPropagation(); setCreatorOpen((v) => !v); }}
          >
            Нова колекція
          </button>
        </div>

        {/* Створення нової колекції */}
        {creatorOpen && (
          <div className="sv-new-row sv-appear" style={{ "--stagger": "36ms" }} onClick={stop}>
            <div className="sv-new-inner">
              <input
                type="text"
                placeholder='Назва (напр., "Авто")'
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onClick={stop}
              />
              <button
                className="sv-btn"
                onClick={(e) => { e.stopPropagation(); handleCreate(); }}
                disabled={creating || !newName.trim()}
              >
                {creating ? "Створення…" : "Створити"}
              </button>
            </div>
          </div>
        )}

        {/* Контент */}
        <div className="sv-content">
          {loading && <div className="sv-empty sv-appear" style={{ "--stagger": "56ms" }}>Завантаження…</div>}
          {!loading && err && <div className="sv-error sv-appear" style={{ "--stagger": "56ms" }}>{err}</div>}

          {!loading && !err && (
            <ul className="sv-list">
              {collections.map((c, i) => {
                const isActive = currentCol === c.collectionId;
                return (
                  <li
                    key={c.collectionId}
                    className="sv-item sv-appear"
                    style={{ "--stagger": `${i * 24}ms` }}
                    onClick={stop}
                  >
                    <div className="sv-item-left">
                      <div className="sv-item-cover" />
                      <div className="sv-item-meta">
                        <div className="sv-item-name">{c.name}</div>
                        <div className="sv-item-sub">Приватна</div>
                      </div>
                    </div>
                    <button
                      className={`sv-bookmark ${isActive ? "is-active" : ""}`}
                      onClick={(e) => { e.stopPropagation(); doSaveTo(c.collectionId); }}
                      title={isActive ? "Прибрати із цієї колекції" : "Додати до цієї колекції"}
                      aria-pressed={isActive}
                    >
                      <img src={isActive ? savedIcon : saveIcon} alt={isActive ? "У колекції" : "Додати"} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
